import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RestApi from '../../Apis/RestApi';
import QRCodeStyling from 'qr-code-styling';
import { nanoid } from 'nanoid';

// Import style images unchanged
import roundedStyle from '../../Assets/DotsStyles/rounded.png';
import dotsStyle from '../../Assets/DotsStyles/dots.png';
import classyStyle from '../../Assets/DotsStyles/classy.png';
import classyRoundedStyle from '../../Assets/DotsStyles/classy-rounded.png';
import squareStyle from '../../Assets/DotsStyles/square.png';
import extraRoundedStyle from '../../Assets/DotsStyles/extra-rounded.png';

// Style images mapping unchanged
const styleImages = {
  rounded: roundedStyle,
  dots: dotsStyle,
  classy: classyStyle,
  'classy-rounded': classyRoundedStyle,
  square: squareStyle,
  'extra-rounded': extraRoundedStyle
};

function QrCodePopUp({ setCreateQR, selectedQR, edit }) {
  const [selectStyle, setSelectStyle] = useState("square");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [squareColor, setSquareColor] = useState("#000000");
  const [pixelColor, setPixelColor] = useState("#000000");
  const [logo, setLogo] = useState(null);
  const [link, setLink] = useState(edit ? selectedQR?.qr_url : "");
  const [userType, setUserType] = useState(null);
  const navigate = useNavigate();
  const qrCodeRef = useRef(null);
  const [qrCodeName, setQrCodeName] = useState(edit ? selectedQR.qr_name ? selectedQR.qr_name : selectedQR.qr_url : "");
  const [qrBase64, setQrBase64] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const accountType = localStorage.getItem("type");
  const userId = localStorage.getItem('_id');
  
  // FIX 1: Use useRef to create a stable short_id reference that won't change between renders
  const shortIdRef = useRef(edit ? selectedQR.qr_short_id : nanoid(8));

  useEffect(() => {
    const storedType = localStorage.getItem('type');
    setUserType(storedType);
    
    // Check QR code limits on component mount for new QR codes
    if (!edit) {
      checkUserQRLimit();
    }
  }, [edit]);

  // Function to check if user has reached their QR code limit - unchanged
  const checkUserQRLimit = async () => {
    try {
      setIsLoading(true);
      const response = await RestApi.getUserQRCount(userId);
      
      if (response.data) {
        // Check for limit reached in successful responses with canCreate=false
        if (
          (response.data.status === false && response.data.err === "Subscription limit reached") ||
          (response.data.status === true && response.data.data && response.data.data.canCreate === false)
        ) {
          setLimitReached(true);
          // Use appropriate message based on response structure
          const errorMsg = response.data.status === false 
            ? (response.data.msg || "You have reached your QR code limit.")
            : "You are currently using all of your allowed QR codes.";
          setLimitError(errorMsg);
        }
      }
    } catch (error) {
      console.error("Error checking QR limits:", error);
      // Don't block the UI on check error, we'll check again before save
    } finally {
      setIsLoading(false);
    }
  };

  const generateQRCode = useCallback(() => {
    if (!qrCodeRef.current) return;
    
    try {
      // FIX 3: Always use the same shortIdRef.current for generating the QR code
      const Rlink = `https://s-qc.in/redirect/${shortIdRef.current}`;

      const qrCode = new QRCodeStyling({
        width: 768,
        height: 768,
        data: Rlink,
        margin: 50,
        dotsOptions: {
          color: pixelColor,
          type: selectStyle,
        },
        backgroundOptions: {
          color: backgroundColor,
        },
        cornersSquareOptions: {
          color: pixelColor,
        },
        image: logo,
      });

      qrCodeRef.current.innerHTML = '';
      qrCode.append(qrCodeRef.current);
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast.error("Failed to generate QR code preview");
    }
  }, [link, selectStyle, backgroundColor, squareColor, pixelColor, logo]);  // FIX 4: Remove short_id from dependencies

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  useEffect(() => {
    const canvas = qrCodeRef.current?.querySelector('canvas');
    if (canvas) {
      // Set your desired height and width here
      canvas.style.height = '220px'; // Change to desired height
      canvas.style.width = '220px'; // Change to desired width
    }
  }, [qrCodeRef, generateQRCode]);

  const handleStyleSelection = (option) => {
    setSelectStyle(option);
  };
  
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    
    // Validate file
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo file is too large. Maximum size is 2MB.");
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toast.error("Invalid file type. Please use JPEG, PNG, SVG or GIF.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.onerror = () => {
        toast.error("Error reading file. Please try again.");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (link === '') {
      toast.error("Link can't be empty.");
      return;
    }
    
    if (qrCodeName === '') {
      toast.error("QR Code name can't be empty.");
      return;
    }
    
    // Basic URL validation
    try {
      // Add protocol if missing
      let urlToCheck = link;
      if (!/^https?:\/\//i.test(urlToCheck)) {
        urlToCheck = 'http://' + urlToCheck;
      }
      new URL(urlToCheck);
    } catch (error) {
      toast.error("Please enter a valid URL.");
      return;
    }
    
    // If creating a new QR code (not editing), check limits again before proceeding
    if (!edit) {
      setIsLoading(true);
      try {
        const limitResponse = await RestApi.getUserQRCount(userId);
        if (limitResponse.data) {
          if (
            (limitResponse.data.status === false && limitResponse.data.err === "Subscription limit reached") ||
            (limitResponse.data.status === true && limitResponse.data.data && limitResponse.data.data.canCreate === false)
          ) {
            setLimitReached(true);
            const errorMsg = limitResponse.data.status === false 
              ? (limitResponse.data.msg || "You have reached your QR code limit.")
              : "You are currently using all of your allowed QR codes.";
            setLimitError(errorMsg);
            setIsLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking limits:", error);
        // Continue and let the create API handle any limit errors
      } finally {
        setIsLoading(false);
      }
    }
    
    const options = {
      link,
      selectStyle,
      backgroundColor,
      squareColor,
      pixelColor,
      logo,
    };
    handleGenerateQR(options);
  };

  const handleGenerateQR = async (options) => {
    setIsLoading(true);
    
    try {
      if (edit) {
        const data = {
          qr_short_id: selectedQR.qr_short_id,
          qr_url: options.link || selectedQR.qr_url,
          qr_name: qrCodeName
        };
  
        const response = await RestApi.updateQr(data);
        if (response.data.status) {
          toast.success("QR Code updated successfully");
          setCreateQR(false);
        } else {
          toast.error(response.data.msg || "Error updating QR Code. Please try again later!");
        }
      } else {
        const canvas = qrCodeRef.current?.querySelector('canvas');
        if (!canvas) {
          toast.error("Failed to generate QR code. Please try again.");
          setIsLoading(false);
          return;
        }
        
        const base64data = canvas.toDataURL('image/png');
        setQrBase64(base64data);
        
        console.log("Saving QR code with short id:", shortIdRef.current);
  
        const data = {
          user_id: userId,
          qr_short_id: shortIdRef.current,
          qr_url: options.link,
          qr_type: 'dynamic',
          qr_image: base64data,
          qr_name: qrCodeName
        };
  
        const response = await RestApi.createQR(data);
        if (response.data.status) {
          toast.success("QR Code created successfully");
          setCreateQR(false);
        } else {
          // Check for subscription limit error in response
          if (response.data.err === "Subscription limit reached" || 
              (response.data.msg && response.data.msg.includes("limit"))) {
            setLimitReached(true);
            setLimitError(response.data.msg || "You have reached your QR code limit.");
            return;
          } else {
            toast.error(response.data.msg || 'Error generating QR Code. Please try again later.');
          }
        }
      }
    } catch (error) {
      console.error("Error in handleGenerateQR:", error);
      
      // Check for 403 error (Forbidden) which indicates subscription limit
      if (error.response && error.response.status === 403) {
        setLimitReached(true);
        const errorMsg = error.response.data?.msg || "You have reached your QR code limit.";
        setLimitError(errorMsg);
      } 
      // Handle 500 errors where the message includes "limit"
      else if (error.response && 
               error.response.data && 
               error.response.data.msg && 
               error.response.data.msg.includes("limit")) {
        setLimitReached(true);
        setLimitError(error.response.data.msg);
      } 
      else {
        toast.error("An error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const linkHandler = (e) => {
    e.preventDefault();
    setLink(e.target.value);
  };

  // Rest of the component remains unchanged...
  // Render subscription limit reached message
  if (limitReached) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10 p-4">
        <div className="bg-[#223153] rounded-lg p-6 w-full max-w-md">
          <div className="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-yellow-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h2 className="text-xl font-bold text-white mb-4">Subscription Limit Reached</h2>
            <p className="text-white mb-6">{limitError || "You are currently using all of your allowed QR codes."}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => navigate('/premium')}
                className="bg-yellow-500 text-black py-2 px-4 rounded-lg hover:bg-yellow-600"
              >
                Upgrade Plan
              </button>
              <button
                onClick={() => setCreateQR(false)}
                className="bg-slate-500 text-black py-2 px-4 hover:bg-slate-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10 p-4 overflow-y-hidden-hidden">
      <div className="relative overflow-y-hidden-hidden">
        <button
          className="absolute -top-4 -right-4 text-white bg-gray-700 hover:bg-gray-600 p-2 z-[999] rounded-full"
          onClick={() => setCreateQR(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className={`bg-[#223153] rounded-lg p-6 w-full max-w-5xl lg:max-h-[95vh] md:max-h-[80vh] max-h-[75vh] overflow-y-auto ${edit ? "max-w-lg min-w-[50vw]" : ""} relative`}>
          <div className="flex flex-col lg:flex-row">
            <div className={`${edit ? "w-full " : "w-full lg:w-[50%] lg:mr-4"}`}>
              <h2 className="text-lg font-bold text-white mb-4">
                {edit ? "Edit your QR Code" : "Add New QR Code Link"}
              </h2>
              <input
                type="text"
                placeholder="Name"
                value={qrCodeName}
                onChange={(e) => setQrCodeName(e.target.value)}
                className="w-full px-3 py-2 border text-white bg-slate-600 border-gray-800 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Enter the link"
                value={link}
                onChange={linkHandler}
                className="w-full px-3 py-2 border text-white bg-slate-600 border-gray-800 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {!edit ? <div className="mt-6 flex justify-center items-center min-h-72 bg-gray-400 rounded-lg">
                <div ref={qrCodeRef}></div>
              </div> :
                <div className='flex justify-center'>
                  <img src={selectedQR?.qr_image} alt="Smart Qr Codes" className='h-1/2 w-1/2'></img>
                </div>
              }
              {(edit || userType === "Free") && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setCreateQR(false)}
                    className="bg-slate-500 text-black p-2 hover:bg-slate-700 rounded-lg px-[10px] m-2"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className={`bg-blue-800 text-white p-2 hover:bg-blue-600 rounded-lg px-[10px] m-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : userType === "Free" ? "Generate QR" : "Save"}
                  </button>
                </div>
              )}
            </div>

            {!edit && (
              <div className={`w-full lg:w-[58%] bg-[#17243b] rounded-lg p-2 relative mt-4 lg:mt-0`}>
                <h2 className="text-white text-lg font-bold p-2">Customize Option</h2>

                <div className="rounded-lg p-2">
                  <h3 className="text-white underline underline-offset-3 mb-2">Style and Margin</h3>
                  <div className="flex flex-wrap justify-center">
                    {Object.keys(styleImages).map((style) => (
                      <div
                        key={style}
                        className={`m-1 cursor-pointer rounded-lg ${selectStyle === style ? "border-2 border-green-600" : ""}`}
                        onClick={() => handleStyleSelection(style)}
                      >
                        <img 
                          src={styleImages[style]} 
                          className="rounded-lg w-24 h-24 object-cover" 
                          alt={`${style} style`} 
                        />
                        <p className={`text-center text-white text-sm ${selectStyle === style ? "bg-green-600" : ""}`}>
                          {style.replace('-', ' ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg p-2">
                  <h3 className="text-white underline underline-offset-4 mb-2">Color</h3>
                  <div className="flex flex-wrap justify-evenly p-1">
                    {[
                      { label: 'Background', value: backgroundColor, setter: setBackgroundColor },
                      { label: 'Pixels', value: pixelColor, setter: setPixelColor },
                    ].map(({ label, value, setter }) => (
                      <div key={label} className="w-full sm:w-2/6 mb-2">
                        <label className="text-white block">{label}</label>
                        <input
                          type="color"
                          value={value}
                          onChange={(e) => setter(e.target.value)}
                          className="w-full h-8 border-none cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg p-2">
                  <h3 className="text-white underline underline-offset-4 mb-2">Logo</h3>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="text-white p-1 w-full"
                    disabled={isLoading}
                  />
                  {logo && (
                    <div className="p-1 mt-2">
                      <img src={logo} alt="Logo preview" className="h-16 w-16 object-contain" />
                      <button
                        onClick={() => setLogo(null)}
                        className="bg-[#9d3434] text-white py-1 px-4 rounded-lg hover:bg-red-700 mt-2"
                        disabled={isLoading}
                      >
                        Remove Logo
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setCreateQR(false)}
                    className="bg-slate-500 text-black p-2 hover:bg-slate-700 rounded-lg px-[10px] m-2"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className={`bg-blue-800 text-white p-2 hover:bg-blue-600 rounded-lg px-[10px] m-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Save'}
                  </button>
                </div>

                {(userType === 'Free' && !edit) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75 z-20 rounded-lg">
                    <div className="text-white text-center">
                      <h3 className="text-2xl mb-4">Unlock Premium Features!</h3>
                      <button
                        className="bg-yellow-500 text-black py-2 px-4 rounded-lg hover:bg-yellow-600"
                        onClick={() => navigate('/premium')}
                      >
                        Buy Premium
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 z-30 rounded-lg">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className="text-white mt-4">Processing...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QrCodePopUp;